// Combat damage formulas — pure functions, no Vue, no component `this`.
//
// This module is the single source of truth for damage math, extracted from
// the near-duplicate copies that lived in inlineUnit.vue and hoverUnit.vue.
//
// END-STATE ARCHITECTURE (being built incrementally — see the phased plan):
//   The public entry point will be
//       computeDamage(attacker, action, defender?, context?)
//   where:
//     - attacker: { pa, ma, speed, brave, faith, paUnarmed, isMartialArts,
//                   doublehand, strengthen:Set<element>, statuses:Set, ... }
//     - action:   { kind:'weapon'|'ability', formula, element, q/k, ... }
//     - defender: { absorb/half/weak:Set<element>, faith, zodiac, statuses:Set }
//     - context:  { attackerStatus:Set, targetStatus:Set, weather, ... }
//   The MOD 2 (physical) and MOD 5 (magic) modifier pipelines from the FFT
//   Battle Mechanics Guide will compose ON TOP of the raw formula functions
//   below. Those formula functions already take explicit inputs (not `this`),
//   so the pipeline wraps them without a rewrite — and status modifiers slot
//   in via `context` without changing this signature.
//
// For now this module exposes the raw weapon-attack formulas only; the
// ability-formula layer and the modifier pipeline land in later phases.

// Weapon ATTACK damage by weapon type (BMG §2.1). Returns:
//   - a number for fixed-damage weapons,
//   - a "min - max" string for random-range weapons (Axe/Bag/Flail),
//   - a "70% (..) - 20% (..) - 10% (..)" string for magical guns,
//   - null when the item type has no attack formula (empty slot, shield,
//     accessory, armor, etc.) so callers can fall back to unarmedDamage().
//
// opts:
//   item        - normalized item ({ type, wp, element }); from itemschanged
//   pa, ma, speed, brave, faith - the attacker's current stats
//   doublehand  - true if the unit has the Doublehand support (2x)
//   targetFaith - defender faith used for magical-gun damage (0-100). Until a
//                 real defender exists, callers pass a placeholder.
//   paBonus     - added to pa/ma/speed (and gun wp) before rolling; used by
//                 Charge+N to model "+N PA".
//   attackUp / magicAttackUp - the attacker's Attack Up / Magic Attack Up
//                 support (×4/3 on the XA term). attackUp scales physical
//                 weapons; magicAttackUp scales the magical (elemental) gun —
//                 the same physical/magical split weaponAttackModel uses, so
//                 the displayed basic-attack number matches the fight view/sim.
//   strengthen  - the attacker's Strengthen element Set (×5/4 on the XA when
//                 the weapon's element is in it).
//   The three attacker modifiers default to no-ops, so the sim/swing/Armory
//   callers (which model these elsewhere, or want a raw figure) are unchanged;
//   only the unit-card / hover display passes them. BMG order on the XA term:
//   Strengthen ×5/4, then Attack/Magic Attack Up ×4/3, each floored.
export function weaponDamage({ item, pa, ma, speed, brave, faith, doublehand = false, targetFaith = 100, paBonus = 0, attackUp = false, magicAttackUp = false, strengthen = null }) {
  const wp = item.wp;
  const paX = pa + paBonus;
  const maX = ma + paBonus;
  const speedX = speed + paBonus;
  const mult = doublehand ? 2 : 1;
  const el = item.element;
  // Apply the attacker XA modifiers to a physical-attack XA term (in BMG order).
  const physXA = (xa) => {
    if (el && strengthen && strengthen.has(el)) xa = Math.floor((xa * 5) / 4);
    if (attackUp) xa = Math.floor((xa * 4) / 3);
    return xa;
  };

  switch (item.type) {
    case 'Rod': case 'Crossbow': case 'Spear': case 'Sword':
      return physXA(paX) * wp * mult;
    case 'Pole': case 'Staff':
      return physXA(maX) * wp * mult;
    case 'Knight Sword': case 'Katana':
      return parseInt(physXA(parseInt(paX * (brave / 100))) * wp) * mult;
    case 'Harp': case 'Book': case 'Fabric':
      return physXA(Math.floor((paX + maX) / 2)) * wp * mult;
    case 'Axe': case 'Bag': case 'Flail':
      return `${1 * wp * mult} - ${physXA(paX) * wp * mult}`;
    case 'Ninja Blade': case 'Knife': case 'Bow':
      return physXA(Math.floor((paX + speedX) / 2)) * wp * mult;
    case 'Gun':
      if (el) {
        const f = faith * targetFaith;
        // Magical gun: Strengthen then Magic Attack Up scale the WP XA term.
        let w = wp + paBonus;
        if (strengthen && strengthen.has(el)) w = Math.floor((w * 5) / 4);
        if (magicAttackUp) w = Math.floor((w * 4) / 3);
        return `70% (${Math.floor((f * 16 * w) / 10000)}) - ` +
               `20% (${Math.floor((f * 20 * w) / 10000)}) - ` +
               `10% (${Math.floor((f * 28 * w) / 10000)})`;
      }
      return physXA(wp + paBonus) * wp;
    default:
      return null;
  }
}

// Unarmed / bare-hands damage (BMG §2.1), also the fallback for weapons with
// no attack formula. Monk and Martial Arts both apply the 1.5x multiplier.
//
// opts:
//   paUnarmed     - attacker PA for unarmed (class base + non-weapon PA gear)
//   brave         - attacker Brave
//   isMartialArts - true for Monk or any unit with the Martial Arts support
//   paBonus       - Charge+N bonus added to the outer PA term
//   attackUp      - Attack Up support (×4/3 on the XA term; default no-op, like
//                   weaponDamage — bare hands is a physical attack)
export function unarmedDamage({ paUnarmed, brave, isMartialArts = false, paBonus = 0, attackUp = false }) {
  const pa = paUnarmed + paBonus;
  let inner = parseInt((paUnarmed * brave) / 100);
  if (attackUp) inner = Math.floor((inner * 4) / 3);
  return parseInt((isMartialArts ? 1.5 : 1) * pa * inner);
}

// ---- Attack as a "model" for the fight-view pipeline ----
// computeAbilityDamage (abilityFormula.js) takes a model with an
// evalFn(ctx, xa) that wraps the XA term in `xa`. These build such a model
// for a weapon ATTACK / unarmed ATTACK so the basic attack runs through the
// same MOD 2/MOD 5 modifier pipeline as abilities (zodiac, target absorb/
// half/weak, defense up, etc.). Weapon attacks are physical (magical:false)
// regardless of which stat they scale — except magical guns (MOD 5).
//
// ctx fields used: pa, ma, speed, wp, brave, faith, targetFaith, paUnarmed.

const _id = (x) => x;

export function weaponAttackModel(item, unit) {
  const mult = unit.SupportSkill === 'Doublehand' ? 2 : 1;
  const element = item.element || null;
  const wp = item.wp; // the weapon's own power (captured, not read from ctx)
  const phys = (stat, evalFn) => ({ kind: 'number', verb: 'Damage', element, magical: false, stat, evalFn });

  switch (item.type) {
    case 'Rod': case 'Crossbow': case 'Spear': case 'Sword':
      return phys('pa', (c, xa = _id) => xa(c.pa) * wp * mult);
    case 'Pole': case 'Staff':
      return phys('ma', (c, xa = _id) => xa(c.ma) * wp * mult);
    case 'Knight Sword': case 'Katana':
      return phys('pa', (c, xa = _id) => xa(Math.floor((c.pa * c.brave) / 100)) * wp * mult);
    case 'Harp': case 'Book': case 'Fabric':
      return phys('pa', (c, xa = _id) => xa(Math.floor((c.pa + c.ma) / 2)) * wp * mult);
    case 'Ninja Blade': case 'Knife': case 'Bow':
      return phys('pa', (c, xa = _id) => xa(Math.floor((c.pa + c.speed) / 2)) * wp * mult);
    case 'Axe': case 'Bag': case 'Flail':
      return phys('pa', (c, xa = _id) => [1 * wp * mult, xa(c.pa) * wp * mult]);
    case 'Gun':
      if (element) {
        // Magical gun (MOD 5). Uses the 70%/level-1 spell constant (Q=16) as
        // the representative number; magical so Magic Attack Up/Shell apply.
        return { kind: 'number', verb: 'Damage', element, magical: true, stat: 'wp',
          evalFn: (c, xa = _id) => Math.floor((c.faith * c.targetFaith * xa(wp) * 16) / 10000) };
      }
      return phys('pa', (c, xa = _id) => xa(wp) * wp * mult); // physical gun: XA = WP
    default:
      return null; // shield / accessory / empty -> caller uses unarmedAttackModel
  }
}

export function unarmedAttackModel(unit) {
  const maMult = (unit.SupportSkill === 'Martial Arts' || unit.Class === 'Monk') ? 1.5 : 1;
  return {
    kind: 'number', verb: 'Damage', element: null, magical: false, stat: 'pa',
    // Martial Arts and the other modifiers scale the OUTER PA, not the [PA*Brave/100] bracket.
    // Checked in FFTBG's own build: a Monk (PA 14, Brave 50, Good zodiac) hit for 182 =
    // ((14 × 3/2 = 21) → 26) × 7, and a Mime (PA 15, Brave 47, Bad zodiac) for 84 = (15 → 12) × 7.
    evalFn: (c, xa = _id) => xa(Math.floor(maMult * c.paUnarmed)) * Math.floor((c.paUnarmed * c.brave) / 100),
  };
}

// JUMP damage. Non-elemental, physical. Armed: PA*WP (×3/2 with a Spear);
// unarmed: the bare-hands brave formula. Per the BMG, Jump ignores Attack Up,
// Martial Arts, and Strengthen, but IS affected by Protect/Defense Up/zodiac
// (so the caller passes empty attacker-mods and the defender mods) and can't
// be evaded.
export function jumpAttackModel(item, unit) {
  if (item && item.wp) {
    const wp = item.wp;
    const k = item.type === 'Spear' ? 1.5 : 1;
    return { kind: 'number', verb: 'Damage', element: null, magical: false, stat: 'pa',
      evalFn: (c, xa = _id) => Math.floor(xa(c.pa) * wp * k) };
  }
  return { kind: 'number', verb: 'Damage', element: null, magical: false, stat: 'pa',
    evalFn: (c, xa = _id) => c.paUnarmed * xa(Math.floor((c.paUnarmed * c.brave) / 100)) };
}
